import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { api } from '@/lib/api'

/**
 * Admins & Roles — Super Admin only (spec Requirement 8).
 *
 * Note this is a **grant**, not an invite. A role can only be given to someone
 * who has already signed in through the normal consumer OAuth flow at least
 * once, matched on `auth_identities.provider_email`. The prototype's "Invite
 * admin" button implied emailed invite tokens; that was dropped deliberately,
 * removing a table, an email integration and token handling from Phase 1.
 */

export const OPS_ROLES = [
  { code: 'ops_admin', label: 'Operations Admin' },
  { code: 'super_admin', label: 'Super Admin' },
] as const

export type OpsRoleCode = (typeof OPS_ROLES)[number]['code']

export const ROLE_LABEL: Record<OpsRoleCode, string> = {
  ops_admin: 'Operations Admin',
  super_admin: 'Super Admin',
}

export const grantSchema = z.object({
  email: z.string().min(1, 'An email is required.').email('That is not a valid email address.'),
  role: z.enum(['ops_admin', 'super_admin']),
})

export type GrantInput = z.infer<typeof grantSchema>

export interface AdminRow {
  /** users.public_id. */
  id: string
  display_name: string
  email: string
  role: OpsRoleCode
  granted_at: string
  granted_by: string
}

/**
 * Whether this screen offers Grant, role-change and Revoke.
 *
 * `false` because the backend mounts `GET /admins` only. The write endpoints
 * are specified and the UI below is built for them, but revoke has to enforce
 * the last-Super-Admin guard inside the write transaction — two Super Admins
 * revoking each other concurrently would otherwise both clear a pre-flight
 * check and lock everyone out of the console, with no in-app way back in.
 *
 * Typed `boolean` rather than left as the literal `false` so the guarded
 * branches below stay type-checked instead of being narrowed away as dead code.
 * Flip to `true` when POST /admins and PATCH /admins/{id} are mounted; the
 * hooks and dialog they need are already here and unchanged.
 */
export const ADMIN_WRITES_ENABLED: boolean = false

const KEY = ['admins']

/** Returns a bare array, not a page — the console has a handful of staff. */
export function useAdmins() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => api.get<AdminRow[]>('/admin/admins'),
  })
}

export function useGrantRole() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: GrantInput) => api.post<{ id: string; role: string }>('/admin/admins', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  })
}

/**
 * Role change and revocation are the same endpoint. Revoking sets `revoked_at`
 * rather than deleting the row, so the history survives.
 */
export function useUpdateAdmin() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; role?: OpsRoleCode; revoke?: boolean }) =>
      api.patch<{ id: string; role: string | null }>(`/admin/admins/${id}`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  })
}

/**
 * The last remaining Super Admin cannot be revoked or downgraded — the backend
 * returns `2002`. Checked here too so the buttons are disabled with a reason
 * rather than failing after the click.
 */
export function isLastSuperAdmin(admins: AdminRow[] | undefined, id: string): boolean {
  if (!admins) return false
  const supers = admins.filter((admin) => admin.role === 'super_admin')
  return supers.length === 1 && supers[0]?.id === id
}
